import React from 'react';
import { Box, Typography } from '@mui/material';
import WidgetWrapper from './WidgetWrapper';

// Helper function to format numbers with decimal and thousands separators
const formatNumber = (value, format) => {
  if (!format || value === null || value === undefined) return value;
  const numValue = Number(value);
  if (isNaN(numValue)) return value;

  let decimals = 2;
  let decimalSeparator = ',';
  let thousandsSeparator = '.';

  // Handle string format like "0.0", "0.00", "0"
  if (typeof format === 'string') {
    const match = format.match(/^0(\.0+)?$/);
    if (match) {
      decimals = match[1] ? match[1].length - 1 : 0;
    }
  } else if (typeof format === 'object') {
    decimals = format.decimals ?? 2;
    decimalSeparator = format.decimalSeparator || ',';
    thousandsSeparator = format.thousandsSeparator || '.';
  }

  const rounded = Number(numValue.toFixed(decimals));
  const [intPart, decPart] = rounded.toFixed(decimals).split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  return decPart ? `${formattedInt}${decimalSeparator}${decPart}` : formattedInt;
};

/**
 * LEDLabel Widget - Combines LED indicator with label and value range display
 * This is a composite widget that recreates the old RMMS "label" widget type
 *
 * Features:
 * - LED indicator that changes color based on value/state
 * - Main title/label text
 * - Tag value display
 * - Min/Max range indicators
 */
const LEDLabel = ({
  mainTitle = 'Sensor',
  text = '',
  tagId = null,
  tagValue = null,
  min = 0,
  max = 100,
  showLED = true,
  showMinMax = true,
  // LED colors based on value ranges
  normalColor = '#4caf50',    // Green - normal range
  warningColor = '#ff9800',   // Orange - warning range
  dangerColor = '#f44336',    // Red - danger range
  offColor = '#9e9e9e',       // Gray - off/no data
  // Thresholds (percentage)
  warningThreshold = 70,
  dangerThreshold = 90,
  // LED size
  ledSize = 20,
  // Number formatting
  numberFormat = null, // { decimals, decimalSeparator, thousandsSeparator }
  unit = '',
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize = 14,
  fontWeight,
  fontFamily,
  opacity,
  ...props
}) => {
  // Use tag value if available, otherwise use static text
  const currentValue = tagValue !== null && tagValue !== undefined ? tagValue : 0;
  const formattedValue = numberFormat ? formatNumber(currentValue, numberFormat) : currentValue;
  const displayText = text || (unit ? `${formattedValue} ${unit}` : String(formattedValue));

  // Determine LED color based on value and thresholds
  const getLEDColor = () => {
    if (tagValue === null || tagValue === undefined) {
      return offColor; // No data
    }

    // Calculate percentage within range
    const percentage = ((currentValue - min) / (max - min)) * 100;

    if (percentage >= dangerThreshold) {
      return dangerColor;
    } else if (percentage >= warningThreshold) {
      return warningColor;
    } else {
      return normalColor;
    }
  };

  const ledColor = getLEDColor();

  // Context menu handler - dispatches global event
  const handleContextMenu = (event) => {
    if (tagId) {
      event.preventDefault();
      event.stopPropagation();

      // Dispatch global event to open Tag Info Modal
      const tagInfoData = {
        tagId: tagId,
        tagName: mainTitle || `LED Label ${tagId}`,
        tagName2: '',
        currentValue: currentValue,
        unit: unit || ''
      };
      window.dispatchEvent(new CustomEvent('openTagInfo', { detail: tagInfoData }));
    } else {
    }
  };

  return (
    <WidgetWrapper
      backgroundColor={backgroundColor}
      foreColor={foreColor}
      borderWidth={borderWidth}
      borderColor={borderColor}
      borderRadius={borderRadius}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontFamily={fontFamily}
      opacity={opacity}
      padding="12px"
      onContextMenu={handleContextMenu}
      {...props}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          width: '100%',
          height: '100%',
          gap: 1
        }}
      >
        {/* Header with LED and Title */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%'
          }}
        >
          {showLED && (
            <Box
              sx={{
                width: ledSize,
                height: ledSize,
                borderRadius: '50%',
                backgroundColor: ledColor,
                boxShadow: `0 0 ${ledSize/2}px ${ledColor}`,
                flexShrink: 0,
                border: '2px solid rgba(0,0,0,0.2)'
              }}
            />
          )}
          <Typography
            sx={{
              fontSize: 'inherit',
              fontWeight: fontWeight || 600,
              fontFamily: 'inherit',
              color: 'inherit',
              flex: 1
            }}
          >
            {mainTitle}
          </Typography>
        </Box>

        {/* Value Display */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            width: '100%'
          }}
        >
          <Typography
            sx={{
              fontSize: `${(fontSize || 14) * 1.5}px`,
              fontWeight: 700,
              fontFamily: 'inherit',
              color: ledColor,
              textAlign: 'center'
            }}
          >
            {displayText}
          </Typography>
        </Box>

        {/* Min/Max Range Display */}
        {showMinMax && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              borderTop: '1px solid rgba(0,0,0,0.1)',
              pt: 0.5
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: `${(fontSize || 14) * 0.75}px`,
                fontFamily: 'inherit',
                color: 'inherit',
                opacity: 0.7
              }}
            >
              Min: {numberFormat ? formatNumber(min, numberFormat) : min}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: `${(fontSize || 14) * 0.75}px`,
                fontFamily: 'inherit',
                color: 'inherit',
                opacity: 0.7
              }}
            >
              Max: {numberFormat ? formatNumber(max, numberFormat) : max}
            </Typography>
          </Box>
        )}
      </Box>
    </WidgetWrapper>
  );
};

export default LEDLabel;
